import type { ScanMode } from '../../pages/pick-pack-station/main/modes';

export const SET_PICK_PACK_STATION_SCAN_MODE = 'SET_PICK_PACK_STATION_SCAN_MODE';
export const setPickPackStationScanMode = (activeMode: ScanMode) => ({
  type: SET_PICK_PACK_STATION_SCAN_MODE,
  activeMode,
});

export const SET_PICK_PACK_STATION_FULFILLMENT_PLAN_ID = 'SET_PICK_PACK_STATION_FULFILLMENT_PLAN_ID';
export const setPickPackStationFulfillmentPlanId = (fulfillmentPlanId: string | null) => ({
  type: SET_PICK_PACK_STATION_FULFILLMENT_PLAN_ID,
  fulfillmentPlanId,
});

// Order search — triggered by user typing or scanning an order number / barcode
export const SEARCH_PICK_PACK_STATION_ORDER = 'SEARCH_PICK_PACK_STATION_ORDER';
export const searchPickPackStationOrder = (searchValue: string) => ({
  type: SEARCH_PICK_PACK_STATION_ORDER,
  searchValue,
});

// Internal: load a specific fulfillment plan after search resolves to one
export const PICK_PACK_STATION_SELECT_FULFILLMENT_PLAN = 'PICK_PACK_STATION_SELECT_FULFILLMENT_PLAN';
export const pickPackStationSelectFulfillmentPlan = (fulfillmentPlanId: string, orderGridStatus?: string) => ({
  type: PICK_PACK_STATION_SELECT_FULFILLMENT_PLAN,
  fulfillmentPlanId,
  orderGridStatus,
});

// Multiple orders matched the search; user must pick one
export const PICK_PACK_STATION_ORDER_SEARCH_RESULTS = 'PICK_PACK_STATION_ORDER_SEARCH_RESULTS';
export const setPickPackStationOrderSearchResults = (orders: unknown[]) => ({
  type: PICK_PACK_STATION_ORDER_SEARCH_RESULTS,
  orders,
});

// Search or load failed
export const PICK_PACK_STATION_ORDER_SEARCH_ERROR = 'PICK_PACK_STATION_ORDER_SEARCH_ERROR';
export const setPickPackStationOrderSearchError = () => ({
  type: PICK_PACK_STATION_ORDER_SEARCH_ERROR,
});

// Update the search input display value without triggering a search (controlled input)
export const SET_PICK_PACK_STATION_SEARCH_INPUT = 'SET_PICK_PACK_STATION_SEARCH_INPUT';
export const setPickPackStationSearchInput = (searchValue: string) => ({
  type: SET_PICK_PACK_STATION_SEARCH_INPUT,
  searchValue,
});

// Clear error / multi-result state; return to ready search input
export const CLEAR_PICK_PACK_STATION_ORDER_SEARCH = 'CLEAR_PICK_PACK_STATION_ORDER_SEARCH';
export const clearPickPackStationOrderSearch = () => ({
  type: CLEAR_PICK_PACK_STATION_ORDER_SEARCH,
});

// All fulfillment plans in the selected order are non-shippable
export const PICK_PACK_STATION_ORDER_CANNOT_BE_SHIPPED = 'PICK_PACK_STATION_ORDER_CANNOT_BE_SHIPPED';
export const pickPackStationOrderCannotBeShipped = (orderGridStatus: string) => ({
  type: PICK_PACK_STATION_ORDER_CANNOT_BE_SHIPPED,
  orderGridStatus,
});

// Unload the current order and return to search input
export const RESET_PICK_PACK_STATION_ORDER = 'RESET_PICK_PACK_STATION_ORDER';
export const resetPickPackStationOrder = () => ({
  type: RESET_PICK_PACK_STATION_ORDER,
});

// Verify individual item — verifiedCount is the new absolute verified count for this item
export const VERIFY_PICK_PACK_STATION_ITEM = 'VERIFY_PICK_PACK_STATION_ITEM';
export const verifyPickPackStationItem = (itemId: string, verifiedCount: number) => ({
  type: VERIFY_PICK_PACK_STATION_ITEM,
  itemId,
  verifiedCount,
});

// Zero out the verified count for a single item
export const CLEAR_PICK_PACK_STATION_ITEM = 'CLEAR_PICK_PACK_STATION_ITEM';
export const clearPickPackStationItem = (itemId: string) => ({
  type: CLEAR_PICK_PACK_STATION_ITEM,
  itemId,
});

// Verify all items at once — replaces the entire verifiedItemCounts map, so callers must pass the complete item list
export const VERIFY_ALL_PICK_PACK_STATION_ITEMS = 'VERIFY_ALL_PICK_PACK_STATION_ITEMS';
export const verifyAllPickPackStationItems = (items: ReadonlyArray<{ itemId: string; quantity: number }>) => ({
  type: VERIFY_ALL_PICK_PACK_STATION_ITEMS,
  items,
});

// Zero out all verified counts
export const CLEAR_ALL_PICK_PACK_STATION_ITEMS = 'CLEAR_ALL_PICK_PACK_STATION_ITEMS';
export const clearAllPickPackStationItems = () => ({
  type: CLEAR_ALL_PICK_PACK_STATION_ITEMS,
});

// Submit verification — triggers epic → POST /verify/fulfillmentPlan {isVerified:true} → success/error
export const SUBMIT_PICK_PACK_STATION_VERIFICATION = 'SUBMIT_PICK_PACK_STATION_VERIFICATION';
export const submitPickPackStationVerification = (fulfillmentPlanId: string) => ({
  type: SUBMIT_PICK_PACK_STATION_VERIFICATION,
  fulfillmentPlanId,
});

export const SUBMIT_PICK_PACK_STATION_VERIFICATION_SUCCESS = 'SUBMIT_PICK_PACK_STATION_VERIFICATION_SUCCESS';
export const submitPickPackStationVerificationSuccess = (fulfillmentPlanId: string) => ({
  type: SUBMIT_PICK_PACK_STATION_VERIFICATION_SUCCESS,
  fulfillmentPlanId,
});

export const SUBMIT_PICK_PACK_STATION_VERIFICATION_ERROR = 'SUBMIT_PICK_PACK_STATION_VERIFICATION_ERROR';
export const submitPickPackStationVerificationError = (fulfillmentPlanId: string) => ({
  type: SUBMIT_PICK_PACK_STATION_VERIFICATION_ERROR,
  fulfillmentPlanId,
});

// Scan an item barcode — epic resolves the match and emits a lastScanResult
export const SCAN_PICK_PACK_STATION_ITEM = 'SCAN_PICK_PACK_STATION_ITEM';
export const scanPickPackStationItem = (productCode: string, itemPreference: string[]) => ({
  type: SCAN_PICK_PACK_STATION_ITEM,
  productCode,
  itemPreference,
});

export type ScanResultKind = 'verified' | 'allVerified' | 'incorrectProduct' | 'extraProduct' | 'scannedOrderNumber';

export type LastScanResult = {
  sequence: number;
  kind: ScanResultKind;
  scannedValue: string;
  itemId: string | null;
};

// Records the outcome of the most recent item scan; sequence increments on every scan
// so components can react via useEffect even when the kind repeats
export const SET_PICK_PACK_STATION_LAST_SCAN_RESULT = 'SET_PICK_PACK_STATION_LAST_SCAN_RESULT';
export const setPickPackStationLastScanResult = (kind: ScanResultKind, scannedValue: string, itemId: string | null = null) => ({
  type: SET_PICK_PACK_STATION_LAST_SCAN_RESULT,
  kind,
  scannedValue,
  itemId,
});

// Seed a batch/tote queue with a list of fulfillment plan IDs to scan in order
export const INITIALIZE_PICK_PACK_STATION_SCAN_QUEUE = 'INITIALIZE_PICK_PACK_STATION_SCAN_QUEUE';
export const initializePickPackStationScanQueue = (fulfillmentPlanIds: string[], batchName: string) => ({
  type: INITIALIZE_PICK_PACK_STATION_SCAN_QUEUE,
  fulfillmentPlanIds,
  batchName,
});

// Move to the next order in the queue (reducer increments currentIndex; epic loads the next plan)
export const ADVANCE_PICK_PACK_STATION_QUEUE = 'ADVANCE_PICK_PACK_STATION_QUEUE';
export const advancePickPackStationQueue = () => ({
  type: ADVANCE_PICK_PACK_STATION_QUEUE,
});

// Reset queue state after all orders in the queue are processed (or user cancels)
export const CLEAR_PICK_PACK_STATION_SCAN_QUEUE = 'CLEAR_PICK_PACK_STATION_SCAN_QUEUE';
export const clearPickPackStationScanQueue = () => ({
  type: CLEAR_PICK_PACK_STATION_SCAN_QUEUE,
});

// Clears isVerified and triggers a server round-trip to mark the order unverified.
// items must be the current fulfillment plan items — they are used to pre-fill verifiedItemCounts
// to full quantity so the packer does not have to re-scan everything from zero.
export const EDIT_PICK_PACK_STATION_VERIFICATION = 'EDIT_PICK_PACK_STATION_VERIFICATION';
export const editPickPackStationVerification = (items: ReadonlyArray<{ itemId: string; quantity: number }> = []) => ({
  type: EDIT_PICK_PACK_STATION_VERIFICATION,
  items,
});

// Trigger label download for the current fulfillment plan; epic resolves plan → fulfillment IDs
export const DOWNLOAD_PICK_PACK_STATION_LABEL = 'DOWNLOAD_PICK_PACK_STATION_LABEL';
export const downloadPickPackStationLabel = (fulfillmentPlanId: string) => ({
  type: DOWNLOAD_PICK_PACK_STATION_LABEL,
  fulfillmentPlanId,
});

// BRIDGE(scan-to-print): reprint existing label via REPRINT_LABEL_IN_SCAN_TO_PRINT pipeline
export const REPRINT_PICK_PACK_STATION_LABEL = 'REPRINT_PICK_PACK_STATION_LABEL';
export const reprintPickPackStationLabel = (fulfillmentPlanId: string) => ({
  type: REPRINT_PICK_PACK_STATION_LABEL,
  fulfillmentPlanId,
});
